import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Plus, Edit, Trash2, Eye, FileText, Newspaper } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get(`${API}/articles/admin/all/`, { params });
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleDelete = async (articleId, title) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${title}" ?`)) {
      try {
        await axios.delete(`${API}/articles/${articleId}/`);
        fetchArticles();
      } catch (error) {
        console.error('Error deleting article:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non publié';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  return (
    <div className="admin-articles-page">
      <div className="admin-articles-container">
        {/* Header */}
        <div className="admin-header">
          <div>
            <h1 className="admin-title">Gestion des Articles</h1>
            <p className="admin-subtitle">Créez et gérez vos articles et actualités</p>
          </div>
          <Button onClick={() => navigate('/admin/articles/new')} size="lg">
            <Plus size={20} />
            Nouvel article
          </Button>
        </div>

        {/* Filters */}
        <div className="admin-filters">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
          >
            Tous ({articles.length})
          </Button>
          <Button
            variant={filter === 'published' ? 'default' : 'outline'}
            onClick={() => setFilter('published')}
          >
            Publiés ({articles.filter(a => a.status === 'published').length})
          </Button>
          <Button
            variant={filter === 'draft' ? 'default' : 'outline'}
            onClick={() => setFilter('draft')}
          >
            Brouillons ({articles.filter(a => a.status === 'draft').length})
          </Button>
        </div>

        {/* Articles List */}
        {loading ? (
          <div className="admin-loading">Chargement...</div>
        ) : articles.length === 0 ? (
          <Card className="admin-empty">
            <FileText size={48} />
            <h3>Aucun article</h3>
            <p>Commencez par créer votre premier article</p>
            <Button onClick={() => navigate('/admin/articles/new')}>
              <Plus size={18} />
              Créer un article
            </Button>
          </Card>
        ) : (
          <div className="admin-articles-list">
            {articles.map((article) => (
              <Card key={article.id} className="admin-article-card">
                <div className="admin-article-info">
                  <div className="admin-article-icon">
                    {article.article_type === 'news' ? (
                      <Newspaper size={24} />
                    ) : (
                      <FileText size={24} />
                    )}
                  </div>
                  <div className="admin-article-details">
                    <h3 className="admin-article-title">{article.title}</h3>
                    <div className="admin-article-meta">
                      <span className={`status-badge ${article.status}`}>
                        {article.status === 'published' ? 'Publié' : 'Brouillon'}
                      </span>
                      <span className="category-badge">{article.category}</span>
                      <span className="date-text">
                        {article.status === 'published' 
                          ? `Publié le ${formatDate(article.published_at)}`
                          : `Créé le ${formatDate(article.created_at)}`
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div className="admin-article-actions">
                  {article.status === 'published' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/articles/${article.slug}`)}
                      title="Voir l'article"
                    >
                      <Eye size={18} />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/admin/articles/edit/${article.id}`)}
                    title="Modifier"
                  >
                    <Edit size={18} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(article.id, article.title)}
                    className="delete-button"
                    title="Supprimer"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminArticles;
